import { useEffect, useState } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useAuth, useBeamer, useQueryParams } from '@/hooks';
import { useHotjar } from '@/hooks/use-hotjar';
import * as Sentry from '@sentry/react';
import { useCheckExtensionInstalled } from '@/hooks/use-check-extension-installed';
import { ExtensionManagerProvider } from './Extensions';
import { useMixpanel } from '@/hooks/use-mixpanel';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { HireflixNotification } from '@/components/ats/job-stage-actions/actions/coding-assessment/assessment/HireflixNotification';
// import { bootstrapCurrentUser } from '@/store/bootstrap/actions';
import { useDispatch } from 'react-redux';
import { Box, Heading } from '@chakra-ui/react';
import { PostInstallPin } from './PostInstallPin';
import { Nudges, useGetRoutes } from './components';
import { checkUserTZMatch } from '@/lib/date';
import { updateMe } from '@/store/me/actions';
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import { NudgeManagerProvider, useShowNudge } from './NudgeManager';
import { useIntercom } from '@/hooks/use-intercom';
// import { findTimeZone } from 'timezone-support';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';
import { useLocationTracker } from '@/hooks/usePreviousLocation';
import { TimezoneSelectionModal } from '@/components/timezone';
import { GlobalBulkOperationQueue } from '@/components/ats/BulkOperationQueue';

export const PrivateRoutes = () => {
  const { currUser } = useAuth();
  const params = useQueryParams();
  const extResult = useCheckExtensionInstalled();
  const { setHotjarUser } = useHotjar();
  const { track } = useMixpanel();
  const ldClient = useLDClient();
  const dispatch = useDispatch();
  const toast = useShowToast();
  const isExternalUser = useIsExternalUser();
  const { showNudge, onCancel } = useShowNudge();
  const flags = useKFlags();
  const { ats_enabled, feInterviewsRevamp } = flags;
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false);
  const [hireflixNotificationData, setHireflixNotificationData] =
    useState(null);

  // Track location at the routing level
  useLocationTracker();

  useEffect(() => {
    window.showHireflixNotification = (data) => {
      setHireflixNotificationData(data);
    };
    return () => {
      delete window.showHireflixNotification;
    };
  }, []);

  useIntercom();

  useEffect(() => {
    if (!ldClient) return;
    const account = window.location.host.split('.')[0];
    ldClient?.identify({ key: `${account}:${currUser?.id}` });
  }, [ldClient, currUser]);

  useEffect(() => {
    if (params.get('extSuccess') === 'true') {
      track({
        event: 'onboarding.extension-installed',
      });
    }
    if (params.get('extUninstalled') === 'true') {
      track({
        event: 'onboarding.extension-uninstalled',
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currUser) return;
    const { id, first_name } = currUser;
    setHotjarUser({
      id,
      name: first_name,
      account: window?.location?.hostname.split('.')[0],
    });
  }, [currUser, setHotjarUser]);

  useBeamer({ currUser });

  const updateTZ = (tz) => {
    return new Promise((resolve, reject) => {
      void (async () => {
        try {
          setIsUpdatingTimezone(true);
          const { errors } = await dispatch(
            updateMe({
              user: {
                time_zone: tz,
              },
            })
          ).unwrap();

          if (!errors?.length) {
            toast({
              title: "We've updated the timezone for you.",
              description: 'You can review it in profile settings.',
              position: 'bottom-left',
              status: 'success',
              duration: 10000,
              isClosable: true,
            });
            // await dispatch(bootstrapCurrentUser());
            // setShowTimezoneModal(false);
            window.location.reload();
            resolve(true);
          } else {
            toast({
              title: 'We are unable to update the timezone for you.',
              description:
                'Kindly update your time zone in profile settings for smoother flow scheduling.',
              position: 'bottom-left',
              status: 'error',
              duration: 30000,
              isClosable: true,
            });
            reject(errors);
          }
        } catch (e) {
          Sentry.captureException(`Unable to update user timezone ${e}`);
          reject(e);
        } finally {
          setIsUpdatingTimezone(false);
        }
      })();
    });
  };

  useEffect(() => {
    if (!currUser) return;
    const { id, first_name, time_zone } = currUser;
    const domain = window?.location?.host?.split('.')?.[0];

    Sentry.configureScope(function (scope) {
      scope.setUser({
        id: id,
        name: first_name,
        domain: domain,
      });
    });
    Sentry.setTag('domain', domain);

    // If user timezone is not set or doesn't match the list we provide, show modal for manual selection.
    // This is to make sure the user timezone always matches the list we provide for consistency.
    if (!checkUserTZMatch(time_zone)) {
      //  // Set suggested timezone
      // const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      //   // we will get the browser timezone and convert it to the canonical timezone name,
      //   // because our Backend needs it in this format
      //   const timeZone = findTimeZone(browserTimeZone)?.name;
      //   if (timeZone) {
      //     // updateTZ(timeZone).catch(); // TODO: Uncomment this once we have a proper way to handle the timezone - For now we are not updating the timezone if there are any discrepancies untill we finalize on the timezone list Ticket Reference - ENG-1372
      //   } else {
      //     // Cannot update time_zone, prompt users to update.
      //   }

      // Show modal for manual timezone selection
      setShowTimezoneModal(true);
    }
  }, [currUser]);

  const allRoutes = useGetRoutes();

  const handleTimezoneSelect = async (timezone) => {
    try {
      await updateTZ(timezone);
    } catch (error) {
      console.error('Failed to update timezone:', error);
    }
  };

  const handleCloseTimezoneModal = () => {
    setShowTimezoneModal(false);
  };

  if (!currUser) return null;

  const renderNotFound = () => {
    return (
      <Box textAlign="center" mt="40px">
        <Heading color="secondary-900" as="h1" fontSize="2xl" mb="4">
          404 - Not Found
        </Heading>
        <p>Oops! The page you&apos;re looking for doesn&apos;t exist.</p>
      </Box>
    );
  };
  const PageTitle = ({ title, skipTitleUpdate = false }) => {
    if (skipTitleUpdate) {
      return null;
    }
    const titleUpdated = title ? `${title}` : 'Kula AI';
    document.title = `${titleUpdated}`;
    return null;
  };

  const filteredRoutes = allRoutes.filter((item) => {
    const { isAllowedToExternalUsers } = item;
    return !isExternalUser || (isExternalUser && isAllowedToExternalUsers);
  });

  return (
    <NudgeManagerProvider value={{ showNudge, onCancel }}>
      <ExtensionManagerProvider value={{ ...extResult }}>
        {!ats_enabled && <Nudges />}
        <Switch>
          {/* todo If all route is removed in getRoutes, move this redirect to bottom */}
          {feInterviewsRevamp && (
            <Redirect from="/interviews/all" to="/interviews/upcoming" />
          )}
          {filteredRoutes.map((item, idx) => {
            const Component = item.component;
            return (
              <Route key={idx} path={item.route} exact={item.exact}>
                <>
                  <PageTitle
                    title={item.title}
                    skipTitleUpdate={item.skipTitleUpdate || false}
                  />
                  <Component />
                </>
              </Route>
            );
          })}
          <Redirect from="/outreaches/:id" to="/flows/:id" />
          <Redirect from="/outreaches" to="/flows" />
          <Redirect from="/reviews" to="/projects/reviews" />
          <Route>{renderNotFound()}</Route>
        </Switch>
        <Box pos="absolute" top={0} right={0}>
          <PostInstallPin />
        </Box>
        {/* Global bulk operations queue - Uses API data (also handles CSV imports) */}
        <GlobalBulkOperationQueue />
        {hireflixNotificationData && currUser && (
          <HireflixNotification
            skippedCandidates={hireflixNotificationData}
            onClose={() => setHireflixNotificationData(null)}
          />
        )}
        {showTimezoneModal && (
          <TimezoneSelectionModal
            isOpen={showTimezoneModal}
            onClose={handleCloseTimezoneModal}
            onTimezoneSelect={handleTimezoneSelect}
            isLoading={isUpdatingTimezone}
          />
        )}
      </ExtensionManagerProvider>
    </NudgeManagerProvider>
  );
};
